/**
 * CommissionAuditPage — التدقيق على العمولة المحصلة (iter-260, Feb 2026)
 * ================================================================
 * Path: /manage/cloudgm-agent/commission-audit
 *
 * Pulls all CLOSED agent tasks that have a collected commission, and
 * lets the auditor upload a bank deposit receipt before marking the
 * entry as "Audited". Filter tabs: Audited | Unaudited | All.
 * Header KPI: Total commissions collected (all rows).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Loader2, Upload, CheckCircle2, XCircle,
  FileDown, RefreshCw, Coins, Search, X, RotateCcw, Eye, Building2, RotateCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell } from './agent/AgentPageShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommissionAuditPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [rows, setRows] = useState([]);
  const [totalCompany, setTotalCompany] = useState(0);
  const [totalAgent, setTotalAgent] = useState(0);
  const [auditedCount, setAuditedCount] = useState(0);
  const [unauditedCount, setUnauditedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | audited | unaudited
  // iter-279d — Split into 2 kinds: new_registrations (default) vs renewals.
  const [kindFilter, setKindFilter] = useState('new_registrations');
  const [q, setQ] = useState('');
  const [busyRow, setBusyRow] = useState('');

  const [uploadingFor, setUploadingFor] = useState('');
  const [pendingFile, setPendingFile] = useState({});   // {taskId: {file_id, filename}}
  const [noteFor, setNoteFor] = useState({});           // {taskId: note}
  const fileInputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kind: kindFilter };
      if (statusFilter === 'audited')   params.status = 'audited';
      if (statusFilter === 'unaudited') params.status = 'unaudited';
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/admin/commission-audit`,
                                { headers: getAuthHeaders(), params });
      setRows(r.data?.data || []);
      setTotalCompany(r.data?.total_company_commission ?? r.data?.total_commission ?? 0);
      setTotalAgent(r.data?.total_agent_commission ?? 0);
      setAuditedCount(r.data?.audited_count || 0);
      setUnauditedCount(r.data?.unaudited_count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kindFilter, q]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (taskId, file) => {
    if (!file) return;
    setUploadingFor(taskId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/file`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const fileEntry = { file_id: r.data.id || r.data.file_id, filename: file.name };
      setPendingFile((prev) => ({ ...prev, [taskId]: fileEntry }));
      toast.success(t('تم رفع الإيصال', 'Receipt uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setUploadingFor('');
      if (fileInputRefs.current[taskId]) fileInputRefs.current[taskId].value = '';
    }
  };

  const markAudited = async (row) => {
    const file = pendingFile[row.id];
    if (!file) {
      toast.error(t('ارفع سند الإيداع أولاً', 'Upload the deposit receipt first'));
      return;
    }
    setBusyRow(row.id);
    try {
      await axios.post(`${API}/admin/commission-audit/${row.id}/mark-audited`, {
        file_id: file.file_id,
        filename: file.filename,
        note: (noteFor[row.id] || '').trim(),
        subject_type: row.subject_type || 'csr',
      }, { headers: getAuthHeaders() });
      toast.success(t('تم التدقيق', 'Marked as audited'));
      setPendingFile((prev) => { const c = { ...prev }; delete c[row.id]; return c; });
      setNoteFor((prev) => { const c = { ...prev }; delete c[row.id]; return c; });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyRow('');
    }
  };

  const unmark = async (row) => {
    if (!window.confirm(t('إلغاء التدقيق لهذا الطلب؟', 'Revert audit for this row?'))) return;
    setBusyRow(row.id);
    try {
      await axios.post(`${API}/admin/commission-audit/${row.id}/unmark`, {}, {
        headers: getAuthHeaders(),
        params: { subject_type: row.subject_type || 'csr' },
      });
      toast.success(t('تم الإلغاء', 'Reverted'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyRow('');
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'التدقيق على العمولة المحصلة', label_en: 'Commission Audit', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent')}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="commission-audit-title">
              {t('التدقيق على العمولة المحصلة', 'Commission Collection Audit')}
            </h1>
            <p className="text-[12px] text-white/50">
              {t('التحقق من العمولات ورفع سندات الإيداع', 'Verify collected commissions and upload deposit receipts')}
            </p>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI icon={Coins}         label={t('عمولة الشركة',  'Company Commission')} value={`${totalCompany.toLocaleString('en-US', { minimumFractionDigits: 2 })} AED`} accent="yellow" testId="kpi-total-company-commission" />
          <KPI icon={Coins}         label={t('عمولة الوكلاء', 'Agent Commission')}   value={`${totalAgent.toLocaleString('en-US',   { minimumFractionDigits: 2 })} AED`} accent="cyan"   testId="kpi-total-agent-commission" />
          <KPI icon={CheckCircle2}  label={t('مدقّق',         'Audited')}            value={auditedCount}   accent="emerald" testId="kpi-audited" />
          <KPI icon={XCircle}       label={t('غير مدقّق',      'Unaudited')}          value={unauditedCount} accent="red"     testId="kpi-unaudited" />
        </div>

        {/* iter-279d — Kind switcher (new registrations vs renewals) */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="commission-audit-kind-tabs">
          {[
            { key: 'new_registrations', ar: 'عمولات تسجيل الشركات الجديدة', en: 'New Company Registrations', icon: Building2 },
            { key: 'renewals',          ar: 'عمولات تجديد الشركات',         en: 'Company Renewals',           icon: RotateCw },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setKindFilter(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition
                  ${kindFilter === tab.key
                    ? 'bg-yellow-500/15 border-yellow-400/60 text-yellow-100'
                    : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                data-testid={`kind-tab-${tab.key}`}
              >
                <Icon className="h-4 w-4" />
                {isAr ? tab.ar : tab.en}
              </button>
            );
          })}
        </div>

        {/* Filter tabs + search */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {[
            { key: 'all',        ar: 'الكل',       en: 'All' },
            { key: 'unaudited',  ar: 'غير مدقّق',  en: 'Unaudited' },
            { key: 'audited',    ar: 'مدقّق',      en: 'Audited' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full border text-[12px] font-semibold
                ${statusFilter === tab.key
                  ? 'bg-yellow-500/20 border-yellow-400 text-yellow-100'
                  : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}
              data-testid={`filter-${tab.key}`}
            >
              {isAr ? tab.ar : tab.en}
            </button>
          ))}
          <div className="relative ms-auto min-w-[240px]">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={t('ابحث برقم الطلب أو اسم الوكيل…', 'Search by request no or agent…')}
                   className="pe-9 h-9"
                   data-testid="search" />
          </div>
          <Button onClick={load} variant="outline" size="sm"
                  className="border-white/10 text-white/70 hover:bg-white/5"
                  data-testid="refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-yellow-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-white/40 text-sm">
              {t('لا توجد بيانات لهذا الفلتر.', 'No rows match this filter.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-xs border-b border-white/5 bg-black/20">
                    <th className="text-start p-3 font-medium">{t('رقم الطلب', 'Request No')}</th>
                    <th className="text-start p-3 font-medium">{t('الشركة / الخدمة', 'Company / Service')}</th>
                    <th className="text-start p-3 font-medium">{t('الوكيل', 'Agent')}</th>
                    <th className="text-start p-3 font-medium">{t('عمولة الشركة', 'Company Commission')}</th>
                    <th className="text-start p-3 font-medium">{t('عمولة الوكيل', 'Agent Commission')}</th>
                    <th className="text-start p-3 font-medium">{t('الفاتورة', 'Invoice')}</th>
                    <th className="text-start p-3 font-medium">{t('حالة التدقيق', 'Audit')}</th>
                    <th className="text-end p-3 font-medium">{t('الإجراءات', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isAudited = r.audit?.audited;
                    const pending = pendingFile[r.id];
                    return (
                      <motion.tr
                        key={r.id}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="border-b border-white/5 hover:bg-white/[0.02] align-top"
                        data-testid={`audit-row-${r.id}`}
                      >
                        <td className="p-3 text-white/90 font-mono text-xs">{r.request_no}</td>
                        <td className="p-3">
                          <div className="text-white/90 text-[12px] font-medium">{r.company_name || '—'}</div>
                          <div className="text-white/50 text-[10px] mt-0.5">{r.service_name || '—'}</div>
                          {r.sub_service_name && (
                            <div className="text-white/40 text-[10px]">↳ {r.sub_service_name}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="text-white/90 text-[12px]">{r.agent?.name || '—'}</div>
                          <div className="text-white/40 text-[10px]" dir="ltr">{r.agent?.email || ''}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-yellow-300 font-bold font-mono" data-testid={`row-company-commission-${r.id}`}>
                            {Number(r.company_commission).toLocaleString('en-US', { minimumFractionDigits: 2 })} AED
                          </div>
                          <div className="text-white/40 text-[10px]" dir="ltr">
                            {t('من الإجمالي', 'of total')}: {Number(r.total_service || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-cyan-300 font-mono text-[12px]" data-testid={`row-agent-commission-${r.id}`}>
                            {Number(r.agent_commission).toLocaleString('en-US', { minimumFractionDigits: 2 })} AED
                          </div>
                        </td>
                        <td className="p-3 text-white/75 font-mono text-[11px]">{r.invoice_no_display || '—'}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border
                            ${isAudited
                              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                              : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}
                            data-testid={`audit-status-${r.id}`}>
                            {isAudited ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                            {isAudited ? t('تم التدقيق', 'Audited') : t('لم يُدقّق', 'Unaudited')}
                          </span>
                          {isAudited && r.audit?.audited_at && (
                            <div className="text-[10px] text-white/40 mt-1">
                              {new Date(r.audit.audited_at).toLocaleDateString('en-GB')}
                            </div>
                          )}
                        </td>
                        <td className="p-3 min-w-[220px]">
                          <div className="flex flex-col items-end gap-2">
                            <button
                              onClick={() => navigate(`/manage/cloudgm-agent/commission-audit/${r.subject_type || 'csr'}/${r.id}`)}
                              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-md bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                              data-testid={`audit-open-detail-${r.id}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              {t('فتح التدقيق', 'Open audit')}
                            </button>
                            {isAudited && (
                              <div className="flex items-center gap-2">
                                {r.audit?.receipt?.file_id && (
                                  <a href={`${API}/upload/file/${r.audit.receipt.file_id}`}
                                     target="_blank" rel="noreferrer"
                                     className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-[11px]"
                                     data-testid={`view-receipt-${r.id}`}>
                                    <FileDown className="h-3 w-3" />
                                    {t('الإيصال', 'Receipt')}
                                  </a>
                                )}
                                <button
                                  onClick={() => unmark(r)}
                                  disabled={busyRow === r.id}
                                  className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 flex items-center gap-1"
                                  data-testid={`unmark-${r.id}`}
                                >
                                  <RotateCcw className="h-2.5 w-2.5" />
                                  {t('إلغاء التدقيق', 'Revert')}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AgentPageShell>
  );
}

function KPI({ icon: Icon, label, value, accent = 'cyan', testId }) {
  const palette = {
    cyan:    'from-cyan-500/10 to-cyan-500/5 border-cyan-500/25 text-cyan-300',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/25 text-emerald-300',
    yellow:  'from-yellow-500/10 to-yellow-500/5 border-yellow-500/25 text-yellow-300',
    red:     'from-red-500/10 to-red-500/5 border-red-500/25 text-red-300',
  }[accent];
  return (
    <div className={`bg-gradient-to-br ${palette} border rounded-xl p-3`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-white/60">{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
    </div>
  );
}
